// apps/client/src/hooks/useUnsubscribes.ts
//
// Unsubscribes screen (Lead Capture > Unsubscribes). One callable,
// `consentAction` (client-functions consent/unsubscribeAction.ts,
// workstream E; super_admin, administrator, marketing):
//
//   searchSubscriber  { email }                  -> SubscriberStatus
//   recordUnsubscribe { email, reason, note }    -> { alreadySuppressed }
//
// Search is by exact address only (no partial match, no listing), so the
// screen can never be used to browse the audience. recordUnsubscribe writes
// the suppression through the existing applySuppression path plus a consent
// withdrawal record; it is idempotent server side.
//
// Spec: docs/plans/2026-10-23-WEBSITE-FORMS-PLATFORM.md §6 and §9.

import { useCallback, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import type { ConsentPurpose } from '@valutten/shared-schemas/dist/consent';
import { functions } from '../lib/firebase';
import { callableErrorMessage, timestampToMillis } from './useWebsiteConnections';

export const CONSENT_ACTION_CALLABLE = 'consentAction';

export const UNSUBSCRIBE_REASONS = ['phone', 'reply', 'in_person', 'other'] as const;
export type UnsubscribeReason = (typeof UNSUBSCRIBE_REASONS)[number];

export const UNSUBSCRIBE_REASON_LABELS: Readonly<Record<UnsubscribeReason, string>> = {
  phone: 'Asked by phone',
  reply: 'Replied to an email',
  in_person: 'Asked in person',
  other: 'Other',
};

export const UNSUBSCRIBE_NOTE_MAX = 500;

/** What the screen shows for one address. */
export interface SubscriberStatus {
  email: string;
  suppressed: boolean;
  suppression: {
    /** 'canonical_mailbox' = suppressed through another spelling that reaches the same inbox. */
    matchedOn: 'address' | 'canonical_mailbox';
    reason: string | null;
    source: string | null;
    /** phone / reply / in_person / other, when recorded on this screen. */
    manualReason: string | null;
    note: string | null;
    atMs: number | null;
  } | null;
  /** Latest state per purpose from consent_state; empty when no record exists. */
  purposes: Partial<Record<ConsentPurpose, { granted: boolean; atMs: number | null }>>;
  /** The eDM contact for this address, if any. */
  contact: { exists: boolean; subscribed: boolean; source: string | null } | null;
}

type Action =
  | { action: 'searchSubscriber'; companyId: string; email: string }
  | { action: 'recordUnsubscribe'; companyId: string; email: string; reason: UnsubscribeReason; note: string };

async function call<T>(payload: Action): Promise<T> {
  const fn = httpsCallable<Action, T>(functions, CONSENT_ACTION_CALLABLE);
  const res = await fn(payload);
  return res.data;
}

/** Trimmed, lower-cased; null when it is not a plausible single address. */
export function normaliseSearchEmail(raw: string): string | null {
  const v = (raw ?? '').trim().toLowerCase();
  if (v.length === 0 || v.length > 254) return null;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? v : null;
}

const strOrNull = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);

/**
 * Shape the raw searchSubscriber response (consent/unsubscribeAction.ts);
 * tolerant of missing fields. `consent` is an array of
 * { purpose, granted, at }; a keyed { purposes } map is also read.
 */
export function normaliseSubscriberStatus(raw: unknown, email: string): SubscriberStatus {
  const r = (raw ?? {}) as {
    email?: unknown;
    suppressed?: unknown;
    suppression?: Record<string, unknown> | null;
    consent?: unknown;
    contact?: Record<string, unknown> | null;
  };
  const s = r.suppression && typeof r.suppression === 'object' ? r.suppression : null;
  const purposes: SubscriberStatus['purposes'] = {};
  const rows: Array<{ purpose?: unknown; granted?: unknown; at?: unknown }> = Array.isArray(r.consent)
    ? (r.consent as Array<{ purpose?: unknown; granted?: unknown; at?: unknown }>)
    : r.consent && typeof r.consent === 'object' && (r.consent as { purposes?: unknown }).purposes
      ? Object.entries((r.consent as { purposes: Record<string, { granted?: unknown; at?: unknown }> }).purposes).map(
          ([purpose, st]) => ({ purpose, ...(st ?? {}) }),
        )
      : [];
  for (const row of rows) {
    if (!row || typeof row.purpose !== 'string') continue;
    purposes[row.purpose as ConsentPurpose] = { granted: row.granted === true, atMs: timestampToMillis(row.at) };
  }
  const c = r.contact && typeof r.contact === 'object' ? r.contact : null;
  return {
    email: typeof r.email === 'string' && r.email ? r.email : email,
    suppressed: r.suppressed === true || s !== null,
    suppression: s
      ? {
          matchedOn: s.matchedOn === 'canonical_mailbox' ? 'canonical_mailbox' : 'address',
          reason: strOrNull(s.reason),
          source: strOrNull(s.source),
          manualReason: strOrNull(s.manualReason),
          note: strOrNull(s.note),
          atMs: timestampToMillis(s.addedAt ?? s.at ?? s.createdAt),
        }
      : null,
    purposes,
    contact: c ? { exists: c.exists === true, subscribed: c.subscribed === true, source: strOrNull(c.source) } : null,
  };
}

export interface UseUnsubscribesResult {
  status: SubscriberStatus | null;
  searching: boolean;
  error: string | null;
  /** Resolves to the status found; validation and server errors land in `error`. */
  search: (rawEmail: string) => Promise<SubscriberStatus | null>;
  /** Rejects with a user-facing Error; on success the status is re-read. */
  recordUnsubscribe: (args: { email: string; reason: UnsubscribeReason; note: string }) => Promise<{ alreadySuppressed: boolean }>;
  clear: () => void;
}

export function useUnsubscribes(companyId: string | undefined): UseUnsubscribesResult {
  const [status, setStatus] = useState<SubscriberStatus | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Only the latest search may set state: a slow earlier answer for a
  // different address must never overwrite the one on screen.
  const seq = useRef(0);

  const search = useCallback(
    async (rawEmail: string): Promise<SubscriberStatus | null> => {
      const email = normaliseSearchEmail(rawEmail);
      const mine = ++seq.current;
      if (!email) {
        setStatus(null);
        setError('Enter one complete email address.');
        return null;
      }
      if (!companyId) {
        setError('No company is selected.');
        return null;
      }
      setSearching(true);
      setError(null);
      try {
        const data = await call<unknown>({ action: 'searchSubscriber', companyId, email });
        const found = normaliseSubscriberStatus(data, email);
        if (mine === seq.current) setStatus(found);
        return found;
      } catch (err) {
        if (mine === seq.current) {
          setStatus(null);
          setError(callableErrorMessage(err, 'The search did not complete. Please try again.'));
        }
        return null;
      } finally {
        if (mine === seq.current) setSearching(false);
      }
    },
    [companyId],
  );

  const recordUnsubscribe = useCallback(
    async (args: { email: string; reason: UnsubscribeReason; note: string }) => {
      if (!companyId) throw new Error('No company is selected.');
      const email = normaliseSearchEmail(args.email);
      if (!email) throw new Error('Enter one complete email address.');
      if (!UNSUBSCRIBE_REASONS.includes(args.reason)) throw new Error('Choose a reason.');
      const note = args.note.trim().slice(0, UNSUBSCRIBE_NOTE_MAX);
      if (args.reason === 'other' && note.length === 0) throw new Error('Add a note when the reason is Other.');
      let alreadySuppressed = false;
      try {
        const data = await call<{ alreadySuppressed?: unknown }>({
          action: 'recordUnsubscribe',
          companyId,
          email,
          reason: args.reason,
          note,
        });
        alreadySuppressed = data?.alreadySuppressed === true;
      } catch (err) {
        throw new Error(callableErrorMessage(err, 'The unsubscribe was not recorded. Please try again.'));
      }
      await search(email);
      return { alreadySuppressed };
    },
    [companyId, search],
  );

  const clear = useCallback(() => {
    seq.current += 1;
    setStatus(null);
    setError(null);
    setSearching(false);
  }, []);

  return { status, searching, error, search, recordUnsubscribe, clear };
}
